/**
 * useSubtaskExpansion Hook
 * Shared hook for managing inline subtask expansion in both
 * cohort detail and cross-cohort task views.
 * MED-479: Inline Subtask Expansion
 */

import { useState, useCallback, useRef } from 'react';
import { tasksApi } from '../services/api/tasks';
import type { TaskWithDetails } from '../types';

const MAX_CONCURRENT_FETCHES = 5;

interface UseSubtaskExpansionReturn {
  /** Set of currently expanded task IDs */
  expandedTaskIds: Set<string>;
  /** Cached subtask data keyed by parent task ID */
  subtaskCache: Record<string, TaskWithDetails[]>;
  /** Set of task IDs currently loading subtasks */
  loadingTaskIds: Set<string>;
  /** Toggle a single task's expansion (lazy-loads on first expand) */
  toggleTask: (taskId: string, cohortId: string) => void;
  /** Expand all parent tasks that have subtasks (replaces expanded set) */
  expandAll: (tasks: Array<{ id: string; cohortId: string; subtaskCount: number }>) => void;
  /** Collapse all expanded tasks (keeps cache) */
  collapseAll: () => void;
  /** Additively expand specific tasks (does not collapse others) */
  expandTasks: (tasks: Array<{ id: string; cohortId: string; subtaskCount: number }>) => void;
  /** Remove specific task IDs from expanded set (does not affect others) */
  collapseTasks: (taskIds: string[]) => void;
  /** Whether any tasks are currently expanded */
  hasExpanded: boolean;
  /** Invalidate cache for a specific task (e.g. after subtask CRUD) */
  invalidateCache: (taskId: string) => void;
  /** Optimistically update a subtask within the cache */
  updateSubtaskInCache: (subtaskId: string, updates: Partial<TaskWithDetails>) => void;
}

export function useSubtaskExpansion(): UseSubtaskExpansionReturn {
  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<string>>(new Set());
  const [subtaskCache, setSubtaskCache] = useState<Record<string, TaskWithDetails[]>>({});
  const [loadingTaskIds, setLoadingTaskIds] = useState<Set<string>>(new Set());

  // Ref to always have latest cache for stale-closure-safe reads
  const cacheRef = useRef(subtaskCache);
  cacheRef.current = subtaskCache;

  const fetchSubtasks = useCallback(async (taskId: string, cohortId: string) => {
    setLoadingTaskIds((prev) => new Set(prev).add(taskId));
    try {
      const taskDetail = await tasksApi.getTaskById(cohortId, taskId);
      setSubtaskCache((prev) => ({ ...prev, [taskId]: taskDetail.subtasks }));
    } catch (error) {
      console.error(`Failed to fetch subtasks for task ${taskId}:`, error);
    } finally {
      setLoadingTaskIds((prev) => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  }, []);

  const toggleTask = useCallback((taskId: string, cohortId: string) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
        if (!cacheRef.current[taskId]) {
          fetchSubtasks(taskId, cohortId);
        }
      }
      return next;
    });
  }, [fetchSubtasks]);

  const expandAll = useCallback(
    (tasks: Array<{ id: string; cohortId: string; subtaskCount: number }>) => {
      const parentTasks = tasks.filter((t) => t.subtaskCount > 0);
      if (parentTasks.length === 0) return;

      // Expand all immediately
      setExpandedTaskIds(new Set(parentTasks.map((t) => t.id)));

      // Fetch all uncached subtasks (fire-and-forget with concurrency limit)
      const uncached = parentTasks.filter((t) => !cacheRef.current[t.id]);
      const fetchBatches = async () => {
        for (let i = 0; i < uncached.length; i += MAX_CONCURRENT_FETCHES) {
          const batch = uncached.slice(i, i + MAX_CONCURRENT_FETCHES);
          await Promise.all(batch.map((t) => fetchSubtasks(t.id, t.cohortId)));
        }
      };
      fetchBatches();
    },
    [fetchSubtasks],
  );

  const collapseAll = useCallback(() => {
    setExpandedTaskIds(new Set());
  }, []);

  // MED-769: Additively expand specific tasks (phase-scoped expand)
  const expandTasks = useCallback(
    (tasks: Array<{ id: string; cohortId: string; subtaskCount: number }>) => {
      const parentTasks = tasks.filter((t) => t.subtaskCount > 0);
      if (parentTasks.length === 0) return;

      setExpandedTaskIds((prev) => {
        const next = new Set(prev);
        parentTasks.forEach((t) => next.add(t.id));
        return next;
      });

      const uncached = parentTasks.filter((t) => !cacheRef.current[t.id]);
      const fetchBatches = async () => {
        for (let i = 0; i < uncached.length; i += MAX_CONCURRENT_FETCHES) {
          const batch = uncached.slice(i, i + MAX_CONCURRENT_FETCHES);
          await Promise.all(batch.map((t) => fetchSubtasks(t.id, t.cohortId)));
        }
      };
      fetchBatches();
    },
    [fetchSubtasks],
  );

  // MED-769: Remove specific task IDs from expanded set (phase-scoped collapse)
  const collapseTasks = useCallback((taskIds: string[]) => {
    setExpandedTaskIds((prev) => {
      const next = new Set(prev);
      taskIds.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const updateSubtaskInCache = useCallback((subtaskId: string, updates: Partial<TaskWithDetails>) => {
    setSubtaskCache((prev) => {
      const next = { ...prev };
      for (const parentId of Object.keys(next)) {
        const subtasks = next[parentId];
        const idx = subtasks.findIndex((s) => s.id === subtaskId);
        if (idx !== -1) {
          next[parentId] = subtasks.map((s) =>
            s.id === subtaskId ? { ...s, ...updates } : s,
          );
          break;
        }
      }
      return next;
    });
  }, []);

  const invalidateCache = useCallback((taskId: string) => {
    setSubtaskCache((prev) => {
      const next = { ...prev };
      delete next[taskId];
      return next;
    });
    // Re-fetch if currently expanded
    setExpandedTaskIds((prev) => {
      if (prev.has(taskId)) {
        // We need the cohortId to re-fetch, but we don't have it here.
        // The caller should handle re-fetching after invalidation if needed.
        // For now, just collapse the invalidated task so it re-fetches on next expand.
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      }
      return prev;
    });
  }, []);

  return {
    expandedTaskIds,
    subtaskCache,
    loadingTaskIds,
    toggleTask,
    expandAll,
    collapseAll,
    expandTasks,
    collapseTasks,
    hasExpanded: expandedTaskIds.size > 0,
    invalidateCache,
    updateSubtaskInCache,
  };
}
